import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", event_date: "", event_time: "", location: "", image_url: "" };

export function EventsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/events").then((res) => setItems(res.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, description: item.description, event_date: item.event_date, event_time: item.event_time || "", location: item.location || "", image_url: item.image_url || "" }); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/admin/events/${editing.id}`, form);
      else await api.post("/admin/events", form);
      toast.success("Saved successfully.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div data-testid="events-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="events-add-button" className="rounded-none bg-accent text-accent-foreground">
          <Plus className="h-4 w-4 mr-2" /> Add Event
        </Button>
      </div>
      <div className="bg-background border border-border divide-y divide-border">
        {items.map((item) => (
          <div key={item.id} data-testid={`events-manager-row-${item.id}`} className="flex items-center gap-4 p-4">
            <div className="flex-1">
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.event_date} · {item.location}</p>
            </div>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`events-edit-${item.id}`} onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`events-delete-${item.id}`} onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted-foreground text-sm">No events yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Event</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input data-testid="events-form-title-input" className="rounded-none mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="events-form-description-input" className="rounded-none mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date (YYYY-MM-DD)</Label><Input data-testid="events-form-date-input" className="rounded-none mt-1.5" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
              <div><Label>Time</Label><Input data-testid="events-form-time-input" className="rounded-none mt-1.5" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} /></div>
            </div>
            <div><Label>Location</Label><Input data-testid="events-form-location-input" className="rounded-none mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div><Label>Image</Label><div className="mt-1.5"><ImageUploadField testId="events-form-image-input" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div></div>
            <Button onClick={handleSave} data-testid="events-form-save-button" className="w-full rounded-none bg-accent text-accent-foreground">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
